/**
 * Role permission matrix. Design doc §8.5 and §15.3.
 *
 * Defined once here and imported by BOTH the Better Auth access-control config (Phase 1)
 * and the API middleware, so the two cannot drift.
 */
import type { Role } from "./enums";

export const ACTIONS = [
  "view",
  "video.write", // upload, rename, delete videos
  "stream.write", // create, edit, delete streams
  "stream.control", // start / stop streams
  "member.manage", // invite, remove, change roles
  "billing.manage", // checkout, manage links, change plan
  "workspace.delete", // delete workspace, transfer ownership
] as const;
export type Action = (typeof ACTIONS)[number];

export const PERMISSIONS: Record<Role, readonly Action[]> = {
  owner: ["view", "video.write", "stream.write", "stream.control", "member.manage", "billing.manage", "workspace.delete"],
  admin: ["view", "video.write", "stream.write", "stream.control", "member.manage", "billing.manage"],
  editor: ["view", "video.write", "stream.write", "stream.control"],
  viewer: ["view"],
};

export function can(role: Role, action: Action): boolean {
  return PERMISSIONS[role].includes(action);
}

/** Roles an actor may hand out when inviting or changing a member. `owner` is never assignable; ownership is transferred separately. */
export function assignableRoles(actor: Role): Role[] {
  return can(actor, "member.manage") ? ["admin", "editor", "viewer"] : [];
}

/** An admin may manage anyone except the owner; the owner may manage anyone else. */
export function canManageMember(actor: Role, target: Role): boolean {
  if (!can(actor, "member.manage")) return false;
  if (target === "owner") return false;
  return true;
}
